import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body2, ComboBox, ConfirmDialog } from "@activecollab/components";
import { List } from "immutable";
import PropTypes from "prop-types";
import Label from "~/records/record.label.jsx";
import { addLabel, addLabelToProject } from "~/store/actions/actions.labels.jsx";
import { hasCollaborativePermissionsOnProject } from "~/store/selectors/selector.users";
import { sortCompare } from "~/utils/utils";

export const SelectTaskLabels = ({ labels, selected, onChange, projectId, onClose, onClear, confirmDeselect }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canEditLabels = useSelector((state) => hasCollaborativePermissionsOnProject(state, projectId));
  const [open, setOpen] = useState();

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    onChange({ labelIds: List(), labelNames: List() });
    setOpen(false);
  }, [onChange]);

  const options = useMemo(
    () =>
      labels
        .toList()
        .toJS()
        .map((label) => ({
          id: label.id,
          name: label.name,
          color: label.lighter_text_color,
          textColor: label.lighter_text_color,
          chipColor: "#303037",
        }))
        .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase())),
    [labels]
  );

  const selectedIds = useMemo(() => selected.toJS(), [selected]);

  const handleChange = useCallback(
    (labelIds) => {
      const labelNames = options.reduce((acc, label) => (labelIds.includes(label.id) ? [...acc, label.name] : acc), []);

      onChange({ labelIds: List(labelIds), labelNames: List(labelNames) });
    },
    [onChange, options]
  );

  const handleAddNewLabel = useCallback(
    (labelName) => {
      const newLabel = {
        name: labelName.toUpperCase(),
        type: "TaskLabel",
        is_global: false,
        is_default: false,
        project_id: projectId,
      };

      dispatch(addLabel(newLabel)).then((data) => {
        const { response } = data;
        if (response.result?.single && response.entities.labels[response.result.single]) {
          const label = new Label(response.entities.labels[response.result.single]);
          dispatch(addLabelToProject(projectId, label));

          const newSelected = [...selected];
          newSelected.push(label.get("id"));

          handleChange(newSelected);
        }
      });
    },
    [dispatch, projectId, selected, handleChange]
  );

  return canEditLabels || selectedIds.length ? (
    <>
      <ComboBox
        type="multiple"
        inputWrapperClassName="tw-w-auto"
        onChange={handleChange}
        selected={selectedIds}
        options={options}
        emptyValue={t("Add new label")}
        handleEmptyAction={handleAddNewLabel}
        placeholder={`${t("Add")}...`}
        scrollWrapper
        disabled={!canEditLabels}
        onClose={onClose}
        onClear={onClear}
        onDeselect={confirmDeselect && handleOpen}
      />
      {confirmDeselect && (
        <ConfirmDialog
          open={open}
          onCancel={handleClose}
          onConfirm={handleConfirm}
          confirmBtnText={t("Clear")}
          cancelBtnText={t("Cancel")}
          dialogTitle={t("Clear All Labels?")}
          dialogContent={t("All labels will be removed from this task.")}
        />
      )}
    </>
  ) : (
    <Body2 color="secondary">{t("No labels")}</Body2>
  );
};

SelectTaskLabels.propTypes = {
  projectId: PropTypes.number.isRequired,
  labels: PropTypes.object.isRequired,
  selected: PropTypes.object,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onClear: PropTypes.func,
  confirmDeselect: PropTypes.bool,
};
